import { useCallback, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faLink } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

export interface ShareButtonProps {
  /** URL copied to the clipboard on click. */
  url: string;
  /** Tooltip / aria-label. */
  label?: string;
  /** Called after a successful copy. */
  onShared?: (url: string) => void;
  className?: string;
  "data-testid"?: string;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Icon button that copies a deep link and briefly confirms with a check. */
export function ShareButton({
  url,
  label = "Copy link to this item",
  onShared,
  className,
  "data-testid": testId = "side-panel-share",
}: ShareButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleClick = useCallback(async () => {
    const ok = await copyToClipboard(url);
    if (!ok) return;
    setCopied(true);
    onShared?.(url);
    window.setTimeout(() => setCopied(false), 1500);
  }, [url, onShared]);

  return (
    <button
      type="button"
      onClick={() => void handleClick()}
      className={clsx(
        "rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-slate-200",
        className
      )}
      aria-label={label}
      title={copied ? "Copied" : label}
      data-testid={testId}
    >
      <FontAwesomeIcon
        icon={copied ? faCheck : faLink}
        className={copied ? "text-emerald-400" : undefined}
      />
    </button>
  );
}
